if (!customElements.get('collection-filters')) {
    class CollectionFilters extends PopupModal {
        #onGridUpdated = null;

        #onFormSubmit = null;

        #onClick = null;

        constructor() {
            super();
        }

        connectedCallback() {
            super.connectedCallback();

            this.elSection = this.closest('section');
            this.elGrid = this.closest('collection-grid');
            this.elToggle = this.elSection?.querySelector('[data-filters-toggle]');
            this.elForm = this.querySelector('.collection-filters--form');
            this.elApply = this.querySelector('.collection-filters--apply');

            this.elToggle?.addEventListener('click', () => this.setAttribute('open', true));
            this.#syncToggleState();

            this.#onFormSubmit = (event) => {
                event.preventDefault();
                this.#setFormBusy(true);
                this.elGrid?.applyFilters(this.elForm)?.finally(() => {
                    this.#setFormBusy(false);
                });
            };

            this.elForm?.addEventListener('submit', this.#onFormSubmit);

            this.#onClick = (event) => {
                const elClear = event.target.closest('[data-clear]');

                if (elClear) {
                    event.preventDefault();
                    this.#clearActiveFilters();
                    this.#clearFilters();
                    return;
                }

                const elLink = event.target.closest('[data-active]');

                if (!elLink?.href) {
                    return;
                }

                event.preventDefault();
                this.#removeActiveFilter(elLink.href);
                this.#setFormBusy(true);
                this.elGrid?.applyFromUrl(elLink.href)?.finally(() => {
                    this.#setFormBusy(false);
                });
            };

            this.elGrid?.addEventListener('click', this.#onClick);

            this.#onGridUpdated = (event) => {
                const source = event.detail?.source;

                if (source !== 'filter' && source !== 'popstate') {
                    return;
                }

                this.#syncFromSection(event.detail.elSection);

                if (source === 'filter') {
                    this.close();
                }
            };

            this.elGrid?.addEventListener('collection-grid:updated', this.#onGridUpdated);

            this.#bindPriceRangeFilters();
        }

        disconnectedCallback() {
            if (this.elForm && this.#onFormSubmit) {
                this.elForm.removeEventListener('submit', this.#onFormSubmit);
            }

            if (this.elGrid && this.#onClick) {
                this.elGrid.removeEventListener('click', this.#onClick);
            }

            if (this.elGrid && this.#onGridUpdated) {
                this.elGrid.removeEventListener('collection-grid:updated', this.#onGridUpdated);
            }
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (name === 'open' && newValue === 'true') {
                this.#syncPromoOffset();
            }

            super.attributeChangedCallback(name, oldValue, newValue);

            if (name === 'open') {
                this.#syncToggleState();
            }
        }

        #syncToggleState() {
            this.elToggle?.setAttribute('aria-expanded', String(this.getAttribute('open') === 'true'));
        }

        #syncPromoOffset() {
            // The promo bar scrolls with the page, so the modal only has to clear
            // whatever of it is still on screen. Scroll is locked while the modal is
            // open, so read it once on open rather than tracking scroll continuously.
            const elPromo = document.querySelector('promo-bar, [data-promo-banner], #FreeShippingBanner');
            const offset = elPromo ? Math.max(0, Math.round(elPromo.getBoundingClientRect().bottom)) : 0;

            this.style.setProperty('--collection-filters-promo-offset', `${offset}px`);
        }

        #setFormBusy(isBusy) {
            if (this.elForm) {
                this.elForm.setAttribute('aria-busy', String(isBusy));
            }

            if (this.elApply) {
                this.elApply.disabled = isBusy;
            }

            for (const elControl of this.elGrid?.querySelectorAll('[data-active], [data-clear]') ?? []) {
                if (elControl.matches('[data-clear]')) {
                    elControl.disabled = isBusy;
                    continue;
                }

                elControl.setAttribute('aria-disabled', String(isBusy));

                if (isBusy) {
                    elControl.style.pointerEvents = 'none';
                } else {
                    elControl.style.pointerEvents = '';
                }
            }
        }

        #removeActiveFilter(removeUrl) {
            const targetHref = new URL(removeUrl, window.location.origin).href;

            for (const elLink of this.elGrid?.querySelectorAll('[data-active]') ?? []) {
                if (new URL(elLink.href, window.location.origin).href !== targetHref) {
                    continue;
                }

                elLink.closest('.collection-filters--active-value')?.remove();
            }

            this.#updateClearButtons();
        }

        #clearActiveFilters() {
            for (const elActive of this.elGrid?.querySelectorAll('.collection-filters--active') ?? []) {
                elActive.querySelector('.collection-filters--active-values')?.replaceChildren();
                elActive.querySelector('[data-clear]')?.remove();
            }
        }

        #updateClearButtons() {
            for (const elActive of this.elGrid?.querySelectorAll('.collection-filters--active') ?? []) {
                const hasValues = elActive.querySelector('.collection-filters--active-value');

                if (!hasValues) {
                    elActive.querySelector('[data-clear]')?.remove();
                }
            }
        }

        #syncFromSection(elSection) {
            if (!elSection) {
                return;
            }

            const elNewFilters = elSection.querySelector('collection-filters');

            if (!elNewFilters) {
                return;
            }

            const elNewForm = elNewFilters.querySelector('.collection-filters--form');

            if (elNewForm && this.elForm) {
                this.elForm.innerHTML = elNewForm.innerHTML;
            }

            for (const className of ['mobile', 'desktop']) {
                const elNewActive = elNewFilters.querySelector(`.collection-filters--active.${className}`);
                const elActive = this.querySelector(`.collection-filters--active.${className}`);

                if (elNewActive && elActive) {
                    elActive.innerHTML = elNewActive.innerHTML;
                }
            }

            const elNewHeaderActive = elSection.querySelector(
                '.collection-grid--filters > .collection-filters--active'
            );
            const elHeaderActive = this.elSection?.querySelector(
                '.collection-grid--filters > .collection-filters--active'
            );

            if (elNewHeaderActive && elHeaderActive) {
                elHeaderActive.innerHTML = elNewHeaderActive.innerHTML;
            }

            this.elApply = this.querySelector('.collection-filters--apply');
            this.#bindPriceRangeFilters();
        }

        #bindPriceRangeFilters() {
            this.elPriceRangeFilters = this.querySelectorAll('[data-price-range-option]');

            for (const el of this.elPriceRangeFilters) {
                el.addEventListener('change', (event) => this.#onPriceRangeFilterChange(event));
            }
        }

        #clearFilters() {
            for (const checkbox of this.elForm.querySelectorAll('input[type="checkbox"]:checked')) {
                checkbox.checked = false;
            }

            for (const radio of this.elForm.querySelectorAll('[data-price-range-option]:checked')) {
                radio.checked = false;
            }

            for (const input of this.elForm.querySelectorAll('[data-price-filter-gte], [data-price-filter-lte]')) {
                input.value = '';
                input.disabled = true;
            }

            this.elForm.requestSubmit();
        }

        #onPriceRangeFilterChange(event) {
            const radio = event.currentTarget;
            const fieldset = radio.closest('fieldset');
            const gteInput = fieldset.querySelector('[data-price-filter-gte]');
            const lteInput = fieldset.querySelector('[data-price-filter-lte]');
            const [min, max] = radio.value.split(':');

            gteInput.disabled = false;
            lteInput.disabled = false;
            gteInput.value = Number(min) / 100;
            lteInput.value = Number(max) / 100;
        }
    }

    customElements.define('collection-filters', CollectionFilters);
}
